/**
 * Item tooltip, slot grid, inventory blocks, trade layout, load meter.
 *
 * One slice of the base-view stylesheet. `styles/index.ts` concatenates
 * every slice into the single sheet `injectStyles` mounts — the split is
 * about OWNERSHIP, not about emitting more than one style element.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below
 * is one template literal and a stray backtick terminates it. That has
 * broken the build twice, both times with esbuild pointing at a line ten
 * rows away from the cause. Write class names in plain text.
 */
export default `
/* == Item tooltip ==========================================================
   ONE floating card per panel (ui/item-tooltip.ts), moved rather than
   rebuilt per slot. position:fixed, NOT absolute: the slot grids scroll
   (overflow-y:auto), and an absolutely positioned card inside one is clipped
   at the grid's edge — the bottom row's tooltip would be cut in half.
   z-index sits above the panel scrims (1001) and the HUD column (1002); a
   tooltip that renders under the thing it describes is worse than none.
   No backticks anywhere in this file's comments - the whole stylesheet is
   one template literal. */
.bv-tip {
  position: fixed; left: 0; top: 0; z-index: 1200;
  width: max-content; max-width: 260px;
  padding: 9px 11px;
  background: linear-gradient(168deg, var(--panel-a), var(--panel-b));
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  box-shadow: 0 10px 28px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06);
  color: var(--bone); font-family: var(--font-technical); font-size: 11px; line-height: 1.5;
  pointer-events: none;
}
.bv-tip[hidden] { display: none; }
.bv-tip-head { display: flex; align-items: center; gap: 8px; justify-content: space-between; margin-bottom: 2px; }
.bv-tip-name {
  font: 700 13px/1.25 var(--font-display); letter-spacing: 0.03em;
  color: var(--brass-hot); text-transform: uppercase;
}
.bv-tip-kind {
  font-size: 10px; letter-spacing: 0.1em; text-transform: uppercase;
  color: var(--bone-dim); margin-bottom: 6px;
}
/* Blocked is a STATE, not a stat: warn-coloured and above the numbers, so
   "you cannot use this" is not read as one more line of gear detail. Not
   .hazard-band - that stripe is reserved for blocked/dangerous, and a full
   band inside a 260px card reads as an error page. */
.bv-tip-blocked {
  margin: 0 0 6px; padding: 3px 6px; border-radius: var(--radius-tape);
  border-left: 3px solid var(--warn); background: rgba(201,138,46,0.12);
  color: var(--warn); font-size: 10px; letter-spacing: 0.04em;
}
.bv-tip-body { display: flex; flex-direction: column; gap: 1px; margin-bottom: 6px; }
.bv-tip-row {
  display: flex; justify-content: space-between; gap: 14px;
  font-variant-numeric: tabular-nums;
}
.bv-tip-row span:first-child { color: var(--bone-dim); }
.bv-tip-row span:last-child { color: var(--bone); }
.bv-tip-row-armor span:last-child { color: #8fb0c4; }
.bv-tip-row-dmg span:last-child { color: var(--brass-bright); }
.bv-tip-row-up span:last-child { color: var(--moss); }
.bv-tip-row-down span:last-child { color: var(--rust); }
.bv-tip-req { color: var(--warn); font-size: 10px; letter-spacing: 0.04em; margin-bottom: 4px; }
.bv-tip-foot {
  border-top: 1px solid var(--line); padding-top: 5px;
  color: var(--bone-faint); font-size: 10px; letter-spacing: 0.04em;
}
.bv-tip-price {
  margin-top: 4px; color: var(--brass-hot); font-size: 11px;
  font-variant-numeric: tabular-nums; letter-spacing: 0.03em;
}

/* == Item slot grid (WoW-shaped) ===========================================
   A square well holding the art at rest, with its actions revealed on hover
   INSIDE the slot rather than pinned under it as a permanent button row.
   Shared by the paperdoll's carried/hideout pools and the vendor's two
   columns, so an item looks the same wherever it is handled.

   The grid is a fixed column count with a max-width, not auto-fill: this is a
   BAG, and a bag whose column count changes with the window is a different
   bag every time you resize it - the muscle memory of "third slot, second
   row" is the whole point of a slot grid. */
.bv-slots {
  display: grid; gap: 8px; align-content: start;
  grid-template-columns: repeat(var(--slot-cols, 5), minmax(0, 1fr));
  max-width: calc(var(--slot-cols, 5) * 78px);
  /* Vertical ONLY. overflow-x:hidden is load-bearing: .bv-pd-panel is
     overflow:auto on both axes, so one over-wide cell used to scroll the
     entire record panel sideways. */
  overflow-y: auto; overflow-x: hidden;
  max-height: 420px; padding-right: 4px;
  scrollbar-width: thin;
}
.bv-slot {
  position: relative; aspect-ratio: 1;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: var(--well);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.55);
  overflow: hidden;
}
.bv-slot-empty { border-style: dashed; border-color: rgba(122,90,48,0.3); }
.bv-slot-filled { cursor: pointer; transition: border-color 0.12s ease, box-shadow 0.12s ease; }
.bv-slot-filled:hover { border-color: var(--brass); box-shadow: inset 0 0 12px rgba(255,189,92,0.14); }
.bv-slot-art {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  padding: 6px;
}
.bv-slot-art img { max-width: 100%; max-height: 100%; object-fit: contain; display: block; }
.bv-slot-art picture { display: contents; }
/* No icon on disk: the name carries the slot instead of an empty square. */
.bv-slot-text {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  padding: 4px; text-align: center; font-size: 9px; line-height: 1.2;
  color: var(--bone-dim); font-family: var(--font-technical);
}
/* Stack count, bottom-right, the corner every inventory in the genre uses. */
.bv-slot-qty {
  position: absolute; right: 2px; bottom: 1px; z-index: 2;
  font: 700 10px/1 var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--bone); text-shadow: 0 1px 2px rgba(0,0,0,0.95), 0 0 3px rgba(0,0,0,0.9);
  pointer-events: none;
}
/* Price, top-left. Trading only - the paperdoll never renders one. */
.bv-slot-price {
  position: absolute; left: 3px; top: 2px; z-index: 2;
  font: 700 9px/1 var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--brass-hot); text-shadow: 0 1px 2px rgba(0,0,0,0.95);
  pointer-events: none;
}
.bv-slot-price { display: inline-flex; align-items: center; gap: 2px; }
/* Per-window allowance on a stall slot ("3 left", "Sold out"). */
.bv-slot-stock {
  position: absolute; left: 3px; bottom: 2px; z-index: 2; padding: 1px 4px; border-radius: 6px;
  font: 700 8px/1.2 var(--font-technical); letter-spacing: 0.04em; text-transform: uppercase;
  color: var(--bone); background: rgba(10,6,3,0.78); border: 1px solid rgba(122,90,48,0.5); pointer-events: none;
}
.bv-slot-worn { border-color: var(--rust); }
.bv-slot-worn .bv-slot-art img { filter: grayscale(0.55) brightness(0.75); }

/* The action overlay. Hidden at rest so the grid reads as ART, which is the
   whole difference between a slot grid and a list of rows with buttons. */
.bv-slot-actions {
  position: absolute; inset: 0; z-index: 3;
  display: flex; flex-direction: column; align-items: stretch; justify-content: flex-end;
  gap: 2px; padding: 3px;
  background: linear-gradient(180deg, rgba(10,7,4,0) 18%, rgba(10,7,4,0.86) 62%);
  opacity: 0; transition: opacity 0.1s ease;
}
.bv-slot-filled:hover .bv-slot-actions,
.bv-slot-filled:focus-within .bv-slot-actions { opacity: 1; }
@media (pointer: coarse) {
  .bv-slot-filled .bv-slot-actions { opacity: 1; }
}
.bv-slot-btn {
  width: 100%; min-height: 32px; padding: 4px 0; cursor: pointer;
  font: 700 10px/1.3 var(--font-display); letter-spacing: 0.06em; text-transform: uppercase;
  touch-action: manipulation;
  color: var(--brass-bright); background: rgba(35,25,14,0.92);
  border: 1px solid var(--line); border-radius: var(--radius-tape);
}
.bv-slot-btn:hover:not(:disabled) { color: var(--brass-hot); border-color: var(--brass-hot); }
.bv-slot-btn:disabled { color: var(--bone-faint); cursor: default; opacity: 0.5; }
.bv-slot-btn-go { color: #14100b; background: linear-gradient(160deg, var(--moss), var(--moss-dark)); border-color: var(--moss); }
.bv-slot-btn-go:hover:not(:disabled) { color: #14100b; background: var(--moss); }
.bv-slot-btn-buy { color: #1a1208; background: linear-gradient(160deg, var(--brass), #8a6a28); border-color: var(--brass); }
.bv-slot-btn-buy:hover:not(:disabled) { color: #1a1208; background: var(--brass-bright); }
.bv-slot-btn-danger { color: var(--rust); }
.bv-slot-btn-danger:hover:not(:disabled) { color: #e07a5f; border-color: var(--rust); }

/* The buy slot's quantity row: a 2-digit input inline with the line total,
   inside the hover overlay. "[2] x 30 Cash" - the price and the amount read
   as one sentence rather than as a number floating above a control. */
.bv-slot-qtyrow {
  display: flex; align-items: center; gap: 4px; width: 100%;
  margin-bottom: 2px;
}
.bv-slot-qtyinput {
  width: 2.4em; flex: none; padding: 1px 2px; text-align: center;
  font: 700 9px/1.3 var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--bone); background: var(--well);
  border: 1px solid var(--line); border-radius: var(--radius-tape);
  -moz-appearance: textfield;
}
.bv-slot-qtyinput::-webkit-outer-spin-button,
.bv-slot-qtyinput::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.bv-slot-qtyinput:focus { outline: none; border-color: var(--brass); }
.bv-slot-linetotal {
  flex: 1 1 auto; min-width: 0; text-align: left;
  font: 9px/1.3 var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--brass-hot); white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

/* == Inventory blocks ======================================================
   Two framed sections instead of two bare columns: ON YOU (the survivor,
   their equipment and the bag they are carrying) and THE HIDEOUT (the stash
   that stays behind). That split is the game's own rule - inventory.md's two
   pools differ in what they cost you when you die - so the panel now draws
   the line the rules already have. */
.bv-inv-blocks { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; align-items: start; }
@media (max-width: 900px) { .bv-inv-blocks { grid-template-columns: 1fr; } }
.bv-block {
  border: 1px solid var(--line); border-radius: var(--radius-panel);
  background: linear-gradient(168deg, rgba(42,33,22,0.55), rgba(16,12,8,0.55));
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.04);
  padding: 10px 12px 12px; min-width: 0;
}
.bv-block-head {
  display: flex; align-items: baseline; justify-content: space-between; gap: 10px;
  margin-bottom: 8px; padding-bottom: 6px; border-bottom: 1px solid var(--line);
}
.bv-block-title {
  font: 700 12px var(--font-display); text-transform: uppercase;
  letter-spacing: 0.12em; color: var(--brass-bright);
}
.bv-block-sub {
  font: 10px var(--font-technical); color: var(--bone-dim);
  letter-spacing: 0.06em; font-variant-numeric: tabular-nums; white-space: nowrap;
}
.bv-block-note { font: 11px var(--font-note); color: var(--bone-faint); padding: 6px 0 0; }

/* == Trade layout ==========================================================
   Player block, trade mark, vendor block. The mark is a real column in the
   grid rather than a decoration on one side, so it stays between the two
   blocks at every width; at the stacked breakpoint it becomes a horizontal
   rule with the mark centred on it, which is what a divider between two
   stacked blocks has to be. */
.bv-trade { display: grid; grid-template-columns: 1fr auto 1fr; gap: 14px; align-items: stretch; }
/* Wider cells than the bag's: the trade slot has to hold a quantity input and
   a line total inside its overlay, which a 78px square cannot do legibly. */
.bv-trade .bv-slots { max-width: calc(var(--slot-cols, 4) * 96px); }
.bv-trade-mark {
  display: flex; align-items: center; justify-content: center;
  align-self: center; width: 76px; height: 76px; flex: none;
  /* Rendered at 54px in --brass at 0.9 it read as a smudge between two bright
     blocks, not as a mark. Bigger, in the bright brass, with a drop shadow to
     lift it off the panel behind it. Seen in a screenshot, not in the CSS. */
  color: var(--brass-bright);
  filter: drop-shadow(0 2px 4px rgba(0,0,0,0.8));
}
.bv-trade-mark svg { width: 100%; height: 100%; display: block; }
@media (max-width: 760px) {
  .bv-trade { grid-template-columns: 1fr; }
  .bv-trade-mark { transform: rotate(90deg); width: 44px; height: 44px; }
}

/* == Load meter (shared by the trade panel and anything else that needs one)
   Same vocabulary as the HUD's carry bar and the paperdoll's three meters -
   the .bv-cap-ok / -warn / -critical pressure classes above drive its fill,
   so one threshold table colours every capacity readout in the game. */
.bv-load { margin-bottom: 10px; }
.bv-load-head {
  display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-bottom: 3px;
}
.bv-load-label {
  font: 700 10px var(--font-display); text-transform: uppercase;
  letter-spacing: 0.1em; color: var(--brass-bright);
}
.bv-load-slots {
  font: 10px var(--font-technical); color: var(--bone-dim);
  letter-spacing: 0.04em; font-variant-numeric: tabular-nums;
}
.bv-load-bar {
  position: relative; height: 13px; border-radius: var(--radius-tape); overflow: hidden;
  background: var(--well); border: 1px solid var(--line);
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.5);
}
.bv-load-fill { height: 100%; transition: width 0.2s ease, background 0.2s ease; }
.bv-load-text {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font: 700 9px var(--font-technical); font-variant-numeric: tabular-nums;
  color: var(--bone); letter-spacing: 0.04em; pointer-events: none;
  text-shadow: 0 1px 2px rgba(0,0,0,0.95);
}
.bv-cap-ok .bv-load-fill { background: linear-gradient(90deg, var(--moss-dark), var(--moss)); }
.bv-cap-warn .bv-load-fill { background: linear-gradient(90deg, #b3712a, var(--warn)); }
.bv-cap-critical .bv-load-fill { background: linear-gradient(90deg, #8f2f1e, var(--rust)); }
.bv-cap-warn .bv-load-slots { color: var(--warn); }
.bv-cap-critical .bv-load-slots { color: var(--rust); }

/* ══ Depth pass (2026-10-13) ════════════════════════════════════════════════
   Slots, blocks, meters and the tooltip. Appended last so source order beats
   the flat rules above at equal specificity; the matching panel-shell pass and
   the shared keyframes (bvpd-*) are at the foot of paperdoll.css.ts, which the
   assembled sheet loads first. Slots are shared with the vendor panel, so
   trading picks the look up too - intended, one bag should look like one bag.

   Idle: every filled slot carries a slow gloss sweep and its art floats a
   pixel or two, each out of phase with its neighbours so the grid shimmers
   instead of marching. Hover: the slot lifts, a brass bracket closes on its
   corners, the art tips toward you, and the actions slide up. */
@keyframes bvslot-gloss {
  0%, 70% { transform: translateX(-160%) skewX(-18deg); }
  100%    { transform: translateX(260%) skewX(-18deg); }
}
@keyframes bvslot-float {
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-1.5px); }
}
@keyframes bvtip-in {
  from { opacity: 0; transform: translateY(4px) scale(0.97); }
  to   { opacity: 1; transform: none; }
}

/* Room for the lift and the glow: the grid clips on both axes. */
.bv-slots { padding: 6px; }
.bv-slot {
  border: 1px solid #4a3920;
  background:
    radial-gradient(circle at 16% 22%, rgba(255,235,200,0.07) 0 1px, transparent 1.5px) 0 0 / 15px 12px,
    radial-gradient(circle at 70% 78%, rgba(0,0,0,0.5) 0 1px, transparent 1.6px) 0 0 / 21px 17px,
    radial-gradient(circle at 50% 38%, #1f160d 0%, #0b0805 90%);
  box-shadow: inset 0 3px 7px rgba(0,0,0,0.85), inset 0 0 0 1px rgba(0,0,0,0.45), 0 1px 0 rgba(255,215,150,0.08);
  transition: transform 0.16s cubic-bezier(0.3, 1.5, 0.5, 1), border-color 0.14s ease, box-shadow 0.14s ease;
}
.bv-slot-empty { border-color: rgba(122,90,48,0.34); box-shadow: inset 0 3px 8px rgba(0,0,0,0.9), 0 1px 0 rgba(255,215,150,0.05); }
.bv-slot-filled {
  border-color: #6b5230;
  background:
    radial-gradient(circle at 16% 22%, rgba(255,235,200,0.08) 0 1px, transparent 1.5px) 0 0 / 15px 12px,
    radial-gradient(circle at 50% 28%, #4d3a22 0%, #1f160d 72%, #0f0a06 100%);
  box-shadow: inset 0 2px 6px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,222,170,0.16), 0 2px 4px rgba(0,0,0,0.5);
}
.bv-slot-filled::before {
  content: ""; position: absolute; inset: 3px; z-index: 2; pointer-events: none;
  opacity: 0; transform: scale(1.14);
  transition: opacity 0.14s ease, transform 0.18s cubic-bezier(0.3, 1.4, 0.5, 1);
  background:
    linear-gradient(var(--brass-hot), var(--brass-hot)) left top / 8px 2px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) left top / 2px 8px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) right top / 8px 2px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) right top / 2px 8px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) left bottom / 8px 2px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) left bottom / 2px 8px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) right bottom / 8px 2px no-repeat,
    linear-gradient(var(--brass-hot), var(--brass-hot)) right bottom / 2px 8px no-repeat;
}
.bv-slot-filled::after {
  content: ""; position: absolute; top: -10%; bottom: -10%; left: 0; width: 34%; z-index: 1;
  pointer-events: none; opacity: 0.9;
  background: linear-gradient(90deg, transparent, rgba(255,230,180,0.2), transparent);
  animation: bvslot-gloss 7s ease-in-out infinite;
}
.bv-slot-filled:nth-child(2n)::after { animation-delay: -1.7s; }
.bv-slot-filled:nth-child(3n)::after { animation-delay: -3.9s; }
.bv-slot-filled:nth-child(5n)::after { animation-delay: -5.2s; }
.bv-slot-art { animation: bvslot-float 5s ease-in-out infinite; }
.bv-slot-filled:nth-child(2n) .bv-slot-art { animation-delay: -1.3s; }
.bv-slot-filled:nth-child(3n) .bv-slot-art { animation-delay: -2.6s; }
.bv-slot-filled:nth-child(5n) .bv-slot-art { animation-delay: -3.8s; }
.bv-slot-art img {
  filter: drop-shadow(0 3px 3px rgba(0,0,0,0.75));
  transition: transform 0.2s cubic-bezier(0.3, 1.6, 0.5, 1), filter 0.16s ease;
}
.bv-slot-filled:hover, .bv-slot-filled:focus-within {
  z-index: 2; transform: translateY(-3px) scale(1.05); border-color: var(--brass-hot);
  box-shadow: inset 0 2px 6px rgba(0,0,0,0.6), inset 0 0 16px rgba(255,189,92,0.2),
              0 0 10px rgba(255,189,92,0.32), 0 6px 10px rgba(0,0,0,0.6);
}
.bv-slot-filled:active { transform: translateY(-1px) scale(0.99); }
.bv-slot-filled:hover::before, .bv-slot-filled:focus-within::before { opacity: 0.9; transform: none; }
.bv-slot-filled:hover .bv-slot-art, .bv-slot-filled:focus-within .bv-slot-art { animation-play-state: paused; }
.bv-slot-filled:hover .bv-slot-art img {
  transform: scale(1.12) rotate(-4deg);
  filter: drop-shadow(0 0 8px rgba(255,189,92,0.55)) drop-shadow(0 3px 3px rgba(0,0,0,0.75));
}
.bv-slot-worn { box-shadow: inset 0 2px 6px rgba(0,0,0,0.7), inset 0 0 12px rgba(168,69,44,0.22), 0 2px 4px rgba(0,0,0,0.5); }
.bv-slot-qty {
  padding: 1px 3px; border-radius: var(--radius-tape);
  background: rgba(10,7,4,0.72); box-shadow: 0 0 0 1px rgba(0,0,0,0.6);
}
/* Actions slide up out of the bottom edge instead of just fading in. */
.bv-slot-actions { transform: translateY(7px); transition: opacity 0.12s ease, transform 0.16s ease; }
.bv-slot-filled:hover .bv-slot-actions,
.bv-slot-filled:focus-within .bv-slot-actions { transform: none; }
.bv-slot-btn {
  background: linear-gradient(180deg, #46361f, #1d1509);
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.2), 0 1px 2px rgba(0,0,0,0.6);
  transition: transform 0.08s ease, filter 0.1s ease, color 0.1s ease, border-color 0.1s ease;
}
.bv-slot-btn:hover:not(:disabled) { filter: brightness(1.2); }
.bv-slot-btn:active:not(:disabled) { transform: translateY(1px); filter: brightness(0.9); }
/* The coloured actions, re-stated AFTER the depth pass above. That pass gives
   every .bv-slot-btn a dark gradient, and at equal specificity the later rule
   wins -- so Buy (and Equip) lost their brass/moss face and rendered as a
   near-black button with near-black text. Janeks, 2026-10-16: "buy buttons
   are black by default". */
.bv-slot-btn-go { background: linear-gradient(180deg, #8fa865, var(--moss-dark)); }
.bv-slot-btn-buy { background: linear-gradient(180deg, #ffd08a 0%, var(--brass-hot) 45%, #9a6c2c 100%); }
.bv-slot-btn-go:disabled, .bv-slot-btn-buy:disabled { color: #1a1208; opacity: 0.55; filter: saturate(0.5); }

/* Blocks: a bolted plate with a groove under its title, not a tinted box. */
.bv-block {
  position: relative; border-color: #4a3920;
  background:
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #1a1208 3.2px, transparent 3.8px) left 6px top 6px / 8px 8px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #1a1208 3.2px, transparent 3.8px) right 6px top 6px / 8px 8px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #1a1208 3.2px, transparent 3.8px) left 6px bottom 6px / 8px 8px no-repeat,
    radial-gradient(circle, #f0d9a8 0 1px, #6b4e22 2px, #1a1208 3.2px, transparent 3.8px) right 6px bottom 6px / 8px 8px no-repeat,
    linear-gradient(180deg, rgba(150,66,26,0.16), transparent) 88% 0 / 18px 90px no-repeat,
    linear-gradient(104deg, transparent 52%, rgba(255,235,200,0.045) 52.3%, transparent 53%) 0 0 / 300px 180px,
    radial-gradient(circle at 20% 30%, rgba(255,235,200,0.05) 0 1px, transparent 1.5px) 0 0 / 17px 13px,
    linear-gradient(168deg, rgba(58,44,28,0.72), rgba(17,13,8,0.72));
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.12), inset 0 -2px 0 rgba(0,0,0,0.5),
              inset 0 0 22px rgba(0,0,0,0.4), 0 3px 8px rgba(0,0,0,0.5);
  padding: 14px 16px 16px;
  transition: border-color 0.16s ease, box-shadow 0.16s ease;
}
.bv-block:hover { border-color: #6b5230; box-shadow: inset 0 1px 0 rgba(255,222,170,0.16), inset 0 -2px 0 rgba(0,0,0,0.5), inset 0 0 22px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.55); }
.bv-block-head { border-bottom: 2px groove #3c301f; padding-left: 4px; }
.bv-block-title { text-shadow: 0 1px 0 #000, 0 0 10px rgba(255,189,92,0.22); }

/* Load meter: deeper groove, graduation ticks over the fill. */
.bv-load-bar {
  height: 14px; border-color: #4a3920;
  box-shadow: inset 0 2px 5px rgba(0,0,0,0.9), 0 1px 0 rgba(255,215,150,0.1);
}
.bv-load-fill { position: relative; overflow: hidden; box-shadow: inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -2px 0 rgba(0,0,0,0.25); }
.bv-load-bar::after {
  content: ""; position: absolute; inset: 0; z-index: 1; pointer-events: none;
  background: repeating-linear-gradient(90deg, transparent 0 9px, rgba(0,0,0,0.42) 9px 10px);
}
.bv-load-text { z-index: 2; }

/* Tooltip: the same worn plate as the panel, and it arrives rather than
   appears. Brass border kept - it is the one thing that says "this is the
   item you are pointing at". */
.bv-tip {
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  background:
    radial-gradient(circle, #f0d9a8 0 0.8px, #6b4e22 1.6px, transparent 2.4px) left 4px top 4px / 6px 6px no-repeat,
    radial-gradient(circle, #f0d9a8 0 0.8px, #6b4e22 1.6px, transparent 2.4px) right 4px top 4px / 6px 6px no-repeat,
    radial-gradient(circle at 20% 28%, rgba(255,235,200,0.06) 0 1px, transparent 1.5px) 0 0 / 14px 11px,
    linear-gradient(180deg, rgba(150,66,26,0.14), transparent) 12% 0 / 18px 60px no-repeat,
    linear-gradient(168deg, #342818, #17100a);
  box-shadow: 0 12px 30px rgba(0,0,0,0.72), 0 0 0 1px #120d08, 0 0 16px rgba(255,189,92,0.14),
              inset 0 1px 0 rgba(255,222,170,0.2), inset 0 0 20px rgba(0,0,0,0.45);
  animation: bvtip-in 0.14s ease-out;
}
.bv-tip-name { text-shadow: 0 1px 0 #000, 0 0 10px rgba(255,189,92,0.3); }

@media (prefers-reduced-motion: reduce) {
  .bv-slot-filled::after, .bv-slot-art, .bv-tip { animation: none; }
  .bv-slot, .bv-slot-filled::before, .bv-slot-art img, .bv-slot-actions,
  .bv-slot-btn, .bv-block { transition: none; }
  .bv-slot-filled:hover, .bv-slot-filled:focus-within,
  .bv-slot-filled:hover .bv-slot-art img { transform: none; }
}

/* A cell past the real slot limit: the grid is 6 x 5 whatever the limit is, and
   a plain empty well there would promise room the server refuses. Hatched and
   dim, with no hover. */
.bv-slot-locked {
  border-style: solid; border-color: #2a2014; cursor: not-allowed;
  background:
    repeating-linear-gradient(135deg, rgba(0,0,0,0.55) 0 5px, rgba(60,44,24,0.35) 5px 10px),
    #0a0705;
  box-shadow: inset 0 2px 6px rgba(0,0,0,0.9);
  opacity: 0.6;
}

/* Pinned card (click a slot): the same card, but it takes the pointer and
   carries the actions. Hover cards stay click-through. */
.bv-tip-pinned { pointer-events: auto; border-color: var(--brass-hot); }
.bv-tip-actions { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; padding-top: 8px; border-top: 1px solid var(--line); }
.bv-tip-actions .bv-slot-btn { position: static; opacity: 1; transform: none; padding: 4px 10px; font-size: 11px; }
.bv-slot-art img, .bv-pd-slot-icon { -webkit-user-drag: none; user-select: none; }
.bv-slot-filled { cursor: pointer; touch-action: pan-y; }
`;
